import { Card, cn } from "@au-schein-de/au-ui-library";
import type { ProductNoEdibleInfo } from "@dransay/api";
import { useLoaderData, useRouteLoaderData } from "react-router";
import type { loader } from "~/routes/_storefront._products.product.$slug.$productId";
import type { Route } from "../../../.react-router/types/app/+types/root";
import type { ExpertReview } from "./expert-review/expert-review.types";
import { ProductDetailsPricing } from "./product-details-pricing.component";
import { ProductDetailsSummary } from "./product-details-summary.component";
import { ProductDetailsUserRating } from "./product-details-user-rating.component";
import type { RatingBreakdownEntry } from "./rating-breakdown-popover.component";
import type { VariantItem } from "./variants/variant-selector.component";
import { VariantSelector } from "./variants/variant-selector.component";

type ProductDetailsCTAProps = {
	product: ProductNoEdibleInfo;
	showExpertReview: boolean;
	expertReview?: ExpertReview;
	onExpertReviewClick?: () => void;
	ratingBreakdown?: ReadonlyArray<RatingBreakdownEntry>;
	localVendorId: string | undefined;
	onLocalVendorChange: (vendorId: string | undefined) => void;
	variants?: VariantItem[];
	effectivePreferredVendorId?: string;
	isDesktopSticky?: boolean;
};

export function ProductDetailsCTA({
	product,
	showExpertReview,
	expertReview,
	onExpertReviewClick,
	ratingBreakdown,
	localVendorId,
	onLocalVendorChange,
	variants,
	effectivePreferredVendorId,
	isDesktopSticky = true,
}: ProductDetailsCTAProps) {
	const { userProductRating } = useLoaderData<typeof loader>();
	const showRatingEditor = userProductRating?.eligible === true;

	return (
		<>
			<div className="md:hidden flex flex-col gap-2">
				<Card.Root className="py-6 flex flex-col gap-3">
					<ProductDetailsSummary
						product={product}
						showExpertReview={showExpertReview}
						expertReview={expertReview}
						onExpertReviewClick={onExpertReviewClick}
						ratingBreakdown={ratingBreakdown}
					/>

					{showRatingEditor && (
						<ProductDetailsUserRating
							productId={product.id}
							initialRating={userProductRating.rating}
						/>
					)}
				</Card.Root>

				{variants && (
					<Card.Root className="py-4 flex flex-col gap-2">
						<VariantSelector
							currentProductId={product.id}
							variants={variants}
							preferredVendorId={effectivePreferredVendorId}
						/>
					</Card.Root>
				)}

				<Card.Root className="py-6 flex flex-col gap-3">
					<ProductDetailsPricing
						product={product}
						localVendorId={localVendorId}
						onLocalVendorChange={onLocalVendorChange}
					/>
				</Card.Root>
			</div>

			<Card.Root
				className={cn(
					"hidden md:flex flex-col h-fit gap-3 py-10",
					isDesktopSticky &&
						"sticky top-(--storefront-cart-offset-desktop) self-start",
				)}
			>
				<ProductDetailsSummary
					product={product}
					showExpertReview={showExpertReview}
					expertReview={expertReview}
					onExpertReviewClick={onExpertReviewClick}
					ratingBreakdown={ratingBreakdown}
				/>

				{showRatingEditor && (
					<ProductDetailsUserRating
						productId={product.id}
						initialRating={userProductRating.rating}
					/>
				)}

				{variants && (
					<VariantSelector
						currentProductId={product.id}
						variants={variants}
						preferredVendorId={effectivePreferredVendorId}
					/>
				)}

				<div className="w-full mt-1">
					<ProductDetailsPricing
						product={product}
						localVendorId={localVendorId}
						onLocalVendorChange={onLocalVendorChange}
					/>
				</div>
			</Card.Root>
		</>
	);
}
